import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Divider,
} from '@mui/material';
import {
  CompareArrows,
  Timeline,
  Fullscreen,
  Download,
  Refresh,
  Warning,
  CheckCircle,
  Schedule,
} from '@mui/icons-material';

interface GanttComparisonViewProps {
  data?: any[];
  loading?: boolean;
}

interface GanttVersion {
  id: string;
  name: string;
  date: string;
  status: 'baseline' | 'current' | 'forecast';
  tasks: GanttTask[];
}

interface GanttTask {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  duration: number;
  progress: number;
  dependencies: string[];
  critical: boolean;
  delayed: boolean;
}

const GanttComparisonView: React.FC<GanttComparisonViewProps> = ({
  data,
  loading = false,
}) => {
  const [selectedVersion1, setSelectedVersion1] = useState('baseline');
  const [selectedVersion2, setSelectedVersion2] = useState('current');
  const [viewMode, setViewMode] = useState<'side-by-side' | 'overlay'>('side-by-side');

  // Mock Gantt versions data
  const mockVersions: GanttVersion[] = [
    {
      id: 'baseline',
      name: 'Baseline Schedule',
      date: '2024-01-01',
      status: 'baseline',
      tasks: [
        {
          id: '1',
          name: 'Site Preparation',
          startDate: '2024-01-01',
          endDate: '2024-01-15',
          duration: 14,
          progress: 100,
          dependencies: [],
          critical: false,
          delayed: false,
        },
        {
          id: '2',
          name: 'Foundation',
          startDate: '2024-01-16',
          endDate: '2024-02-15',
          duration: 30,
          progress: 100,
          dependencies: ['1'],
          critical: true,
          delayed: false,
        },
        {
          id: '3',
          name: 'Framing',
          startDate: '2024-02-16',
          endDate: '2024-03-30',
          duration: 43,
          progress: 75,
          dependencies: ['2'],
          critical: true,
          delayed: false,
        },
        {
          id: '4',
          name: 'Roofing',
          startDate: '2024-04-01',
          endDate: '2024-04-20',
          duration: 19,
          progress: 0,
          dependencies: ['3'],
          critical: false,
          delayed: false,
        },
      ],
    },
    {
      id: 'current',
      name: 'Current Schedule',
      date: '2024-03-15',
      status: 'current',
      tasks: [
        {
          id: '1',
          name: 'Site Preparation',
          startDate: '2024-01-01',
          endDate: '2024-01-18',
          duration: 17,
          progress: 100,
          dependencies: [],
          critical: false,
          delayed: true,
        },
        {
          id: '2',
          name: 'Foundation',
          startDate: '2024-01-19',
          endDate: '2024-02-20',
          duration: 32,
          progress: 100,
          dependencies: ['1'],
          critical: true,
          delayed: true,
        },
        {
          id: '3',
          name: 'Framing',
          startDate: '2024-02-21',
          endDate: '2024-04-05',
          duration: 44,
          progress: 85,
          dependencies: ['2'],
          critical: true,
          delayed: true,
        },
        {
          id: '4',
          name: 'Roofing',
          startDate: '2024-04-06',
          endDate: '2024-04-28',
          duration: 22,
          progress: 0,
          dependencies: ['3'],
          critical: false,
          delayed: true,
        },
      ],
    },
    {
      id: 'forecast',
      name: 'Forecast Schedule',
      date: '2024-03-20',
      status: 'forecast',
      tasks: [
        {
          id: '1',
          name: 'Site Preparation',
          startDate: '2024-01-01',
          endDate: '2024-01-18',
          duration: 17,
          progress: 100,
          dependencies: [],
          critical: false,
          delayed: false,
        },
        {
          id: '2',
          name: 'Foundation',
          startDate: '2024-01-19',
          endDate: '2024-02-20',
          duration: 32,
          progress: 100,
          dependencies: ['1'],
          critical: true,
          delayed: false,
        },
        {
          id: '3',
          name: 'Framing',
          startDate: '2024-02-21',
          endDate: '2024-04-10',
          duration: 49,
          progress: 85,
          dependencies: ['2'],
          critical: true,
          delayed: false,
        },
        {
          id: '4',
          name: 'Roofing',
          startDate: '2024-04-11',
          endDate: '2024-05-05',
          duration: 24,
          progress: 0,
          dependencies: ['3'],
          critical: false,
          delayed: false,
        },
      ],
    },
  ];

  const getVersionById = (id: string) => mockVersions.find(v => v.id === id);
  const version1 = getVersionById(selectedVersion1);
  const version2 = getVersionById(selectedVersion2);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'baseline': return '#1976d2';
      case 'current': return '#388e3c';
      case 'forecast': return '#f57c00';
      default: return '#666';
    }
  };

  const getTaskBarColor = (task: GanttTask, version: GanttVersion) => {
    if (task.delayed) return '#f44336';
    if (task.critical) return '#ff9800';
    return getStatusColor(version.status);
  };

  const calculateTaskPosition = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const projectStart = new Date('2024-01-01');
    const projectEnd = new Date('2024-06-01');
    
    const totalDuration = projectEnd.getTime() - projectStart.getTime();
    const taskStart = start.getTime() - projectStart.getTime();
    const taskDuration = end.getTime() - start.getTime();
    
    const left = (taskStart / totalDuration) * 100;
    const width = (taskDuration / totalDuration) * 100;
    
    return { left: `${left}%`, width: `${width}%` };
  };

  const renderGanttChart = (version: GanttVersion | undefined, title: string) => {
    if (!version) return null;

    return (
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6">{title}</Typography>
          <Chip 
            label={version.name} 
            color={version.status === 'baseline' ? 'primary' : version.status === 'current' ? 'success' : 'warning'}
            size="small"
          />
          <Typography variant="body2" color="text.secondary">
            {version.date}
          </Typography>
        </Box>
        
        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
          {/* Timeline Header */}
          <Box sx={{ display: 'flex', mb: 2, fontSize: '12px', color: 'text.secondary' }}>
            <Box sx={{ width: '200px' }}>Task</Box>
            <Box sx={{ flex: 1, position: 'relative' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Jan 2024</span>
                <span>Feb 2024</span>
                <span>Mar 2024</span>
                <span>Apr 2024</span>
                <span>May 2024</span>
              </Box>
            </Box>
          </Box>
          
          {/* Tasks */}
          {version.tasks.map((task, index) => {
            const position = calculateTaskPosition(task.startDate, task.endDate);
            return (
              <Box key={task.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ width: '200px', pr: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight={task.critical ? 'bold' : 'normal'}>
                      {task.name}
                    </Typography>
                    {task.critical && <Warning color="warning" fontSize="small" />}
                    {task.delayed && <Schedule color="error" fontSize="small" />}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {task.progress}% complete
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, position: 'relative', height: '24px', bgcolor: 'white', borderRadius: 1 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '2px',
                      height: '20px',
                      backgroundColor: getTaskBarColor(task, version),
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      px: 1,
                      ...position,
                    }}
                  >
                    <Box
                      sx={{
                        width: `${task.progress}%`,
                        height: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Paper>
      </Box>
    );
  };

  const renderComparisonInsights = () => {
    if (!version1 || !version2) return null;

    const delayedTasks1 = version1.tasks.filter(t => t.delayed).length;
    const delayedTasks2 = version2.tasks.filter(t => t.delayed).length;
    const avgProgress1 = version1.tasks.reduce((sum, t) => sum + t.progress, 0) / version1.tasks.length;
    const avgProgress2 = version2.tasks.reduce((sum, t) => sum + t.progress, 0) / version2.tasks.length;

    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📊 Comparison Insights
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Paper sx={{ p: 2, flex: 1, bgcolor: delayedTasks2 > delayedTasks1 ? 'error.light' : 'success.light' }}>
              <Typography variant="subtitle2" gutterBottom>
                Schedule Performance
              </Typography>
              <Typography variant="body2">
                {delayedTasks2 > delayedTasks1 
                  ? `${delayedTasks2 - delayedTasks1} more tasks are delayed compared to ${version1.name}`
                  : delayedTasks2 < delayedTasks1
                  ? `${delayedTasks1 - delayedTasks2} fewer tasks are delayed compared to ${version1.name}`
                  : 'Same number of delayed tasks'
                }
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1, bgcolor: avgProgress2 > avgProgress1 ? 'success.light' : 'warning.light' }}>
              <Typography variant="subtitle2" gutterBottom>
                Progress Comparison
              </Typography>
              <Typography variant="body2">
                Average progress: {avgProgress2.toFixed(1)}% vs {avgProgress1.toFixed(1)}%
                ({avgProgress2 > avgProgress1 ? '+' : ''}{(avgProgress2 - avgProgress1).toFixed(1)}%)
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1, bgcolor: 'info.light' }}>
              <Typography variant="subtitle2" gutterBottom>
                AI Recommendation
              </Typography>
              <Typography variant="body2">
                {delayedTasks2 > delayedTasks1 
                  ? 'Consider resource reallocation to critical path tasks'
                  : 'Schedule is improving. Maintain current pace'
                }
              </Typography>
            </Paper>
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Gantt Chart Comparison
          </Typography>
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">
            Gantt Chart Comparison
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Version 1</InputLabel>
              <Select
                value={selectedVersion1}
                label="Version 1"
                onChange={(e) => setSelectedVersion1(e.target.value)}
              >
                {mockVersions.map((version) => (
                  <MenuItem key={version.id} value={version.id}>
                    {version.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <CompareArrows color="primary" />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Version 2</InputLabel>
              <Select
                value={selectedVersion2}
                label="Version 2"
                onChange={(e) => setSelectedVersion2(e.target.value)}
              >
                {mockVersions.map((version) => (
                  <MenuItem key={version.id} value={version.id}>
                    {version.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Divider orientation="vertical" flexItem />
            <Tooltip title="Refresh Data">
              <IconButton size="small">
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download Comparison">
              <IconButton size="small">
                <Download />
              </IconButton>
            </Tooltip>
            <Tooltip title="Fullscreen">
              <IconButton size="small">
                <Fullscreen />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 3 }}>
          {renderGanttChart(version1, 'Version 1')}
          {renderGanttChart(version2, 'Version 2')}
        </Box>

        {renderComparisonInsights()}
      </CardContent>
    </Card>
  );
};

export default GanttComparisonView;